import {
  Autocomplete,
  AutocompleteCloseReason,
  AutocompleteProps,
  Divider,
  FormControl,
  FormControlProps,
  FormHelperText,
  InputProps,
  Paper,
  type SxProps,
  TextField,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
  useThemeProps,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { unstable_setRef as setRef } from '@mui/utils';
import { FORM_FIELD_THEME_NAME } from '@onevision-resources/design-theme';
import { Plus } from 'lucide-react';
import { HTMLAttributes, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { AutoCompleteOption } from 'src/components/Forms/types';
import { useWatchConditions } from 'src/components/Forms/use-validations';
import {
  CreateActionDivider,
  CreateActionRow,
} from 'src/components/SelectMenu/select-menu.elements';
import { Tooltip, TooltipProps } from 'src/components/Tooltip/Tooltip';
import { toSxArray } from 'src/utils/sx';

import { getFormAutocompleteMobileInputZoomSx } from './form-autocomplete-mobile-input-sx';

export type DefaultOption = AutoCompleteOption | string;
export type AutocompleteValue<T extends DefaultOption = DefaultOption> = T;

type PopperSlotProps = { modifiers?: unknown[]; style?: React.CSSProperties };

type ListboxSlotProps = { style?: React.CSSProperties; className?: string };

export type FormAutocompleteProps<T extends DefaultOption = DefaultOption> = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'options' | 'name'
> & {
  options: AutocompleteValue<T>[];
  required?: boolean | string;
  label?: string;
  loading?: boolean;
  openOnFocus?: boolean;
  blurOnSelect?: boolean; // Blur input after selection. Defaults to true.
  blurOnClose?: boolean; // Blur input on close via escape/blur. Defaults to true.
  // TODO: expand value type to support multiple options
  value?: AutocompleteValue<T> | null;
  multiple?: boolean;
  autocompleteProps?: Partial<AutocompleteProps<AutocompleteValue<T>, boolean, true, boolean>>;
  textFieldProps?: TextFieldProps;
  inputProps?: InputProps;
  creatable?: boolean;
  tooltipProps?: Partial<TooltipProps>;
  tooltipText?: string | React.ReactNode;
  onAddNewItem?: () => void;
  onAddNewItemLabel?: string;
  textfieldRef?: React.RefObject<HTMLInputElement | null>;
  underlined?: boolean;
  disabled?: boolean;
  showErrorText?: boolean;
} & CommonFormFieldProps<'autocomplete', AutocompleteValue<T> | null | undefined>;

export const FormAutocomplete = <T extends DefaultOption = DefaultOption>(
  inProps: FormAutocompleteProps<T>,
) => {
  const props = useThemeProps({ props: inProps, name: FORM_FIELD_THEME_NAME });
  const {
    name,
    label,
    required,
    options,
    autocompleteProps,
    textFieldProps,
    inputProps,
    children,
    dependsOn = [],
    showsIf = [],
    defaultValue,
    loading = false,
    multiple = false,
    creatable = false,
    openOnFocus = false,
    blurOnSelect = true,
    blurOnClose = true,
    tooltipProps,
    tooltipText,
    onAddNewItem,
    onAddNewItemLabel,
    textfieldRef,
    underlined = false,
    disabled = false,
    showErrorText = false,
    labelAbove = false,
    size,
    ...rest
  } = props;
  const variant = textFieldProps?.variant ?? (labelAbove ? 'outlined' : 'standard');
  const generatedInputId = useId();
  const inputId = textFieldProps?.id ?? autocompleteProps?.id ?? generatedInputId;

  const { sx: autocompleteSx, ...restAutocompleteProps } = autocompleteProps ?? {};

  const localRef = useRef<HTMLInputElement | null>(null);
  const inputRef = textfieldRef || localRef;
  const lastCloseReasonRef = useRef<AutocompleteCloseReason | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // On desktop, pin the popup inside the viewport (extra right/bottom padding) so a tall
  // list near a scrollable panel edge can't push the page into a flip/scrollbar flicker loop.
  const slotSources = restAutocompleteProps as unknown as {
    slotProps?: { popper?: PopperSlotProps; listbox?: ListboxSlotProps } & Record<
      string,
      unknown
    >;
    componentsProps?: { popper?: PopperSlotProps };
    ListboxProps?: ListboxSlotProps;
  };
  const incomingPopperProps =
    slotSources.slotProps?.popper ?? slotSources.componentsProps?.popper;
  const incomingListboxProps = slotSources.slotProps?.listbox ?? slotSources.ListboxProps;
  const mergedSlotProps = isMobile
    ? { ...slotSources.slotProps, listbox: incomingListboxProps }
    : {
        ...slotSources.slotProps,
        popper: {
          ...incomingPopperProps,
          modifiers: [
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'viewport' as const,
                altAxis: true,
                padding: { top: 8, left: 8, right: 42, bottom: 42 },
              },
            },
            ...(incomingPopperProps?.modifiers ?? []),
          ],
        },
        listbox: {
          ...incomingListboxProps,
          style: { maxHeight: 'min(40vh, 320px)', ...incomingListboxProps?.style },
        },
      };
  const { control, formState, resetField } = useFormContext();
  const errorMsg = formState.errors[name]?.message;
  // If the field depends on another field, reset value when the field it depends on changes.
  const values = useWatch({ control, name: dependsOn });

  const [isOpened, setIsOpened] = useState(false);

  useEffect(() => {
    if (values?.length > 0) {
      resetField(name);
    }
  }, [values, resetField, name]);

  useEffect(() => {
    const closeReason = lastCloseReasonRef.current;
    const shouldBlurOnClose = closeReason === 'escape' || closeReason === 'blur';

    if (!isOpened && blurOnClose && shouldBlurOnClose && inputRef.current) {
      inputRef.current.blur();
    }
  }, [blurOnClose, inputRef, isOpened]);

  useEffect(() => {
    let timer: string | number | NodeJS.Timeout | undefined;
    if (textFieldProps?.autoFocus) {
      timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [textFieldProps?.autoFocus, inputRef]);

  // Having a string type in autocompletes will allow us to use an
  // entity directly in a form, instead of having to transform their
  // values into objects
  const getValue = useCallback(
    <T extends DefaultOption>({ value }: { value?: AutocompleteValue<T> | string }) => {
      const currentValue = typeof value === 'string' ? value : value?.id || value?.value;
      const selectedOption = options.find(
        (option) => (typeof option === 'string' ? option : option.id) === currentValue,
      );

      return selectedOption ?? value;
    },
    [options],
  );

  const shouldShow = useWatchConditions({
    control,
    showsIf,
  });

  if (!shouldShow) {
    return;
  }

  return (
    <FormControl
      variant={variant}
      size={size}
      {...rest}
      sx={{ width: '100%', ...rest.sx }}
      error={!!errorMsg}
    >
      {labelAbove && !!label ? (
        <FormFieldLabel htmlFor={inputId} disabled={disabled}>
          {label}
        </FormFieldLabel>
      ) : null}
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field }) => (
          <Autocomplete
            disabled={disabled}
            size={size}
            multiple={multiple}
            id={labelAbove ? inputId : undefined}
            aria-label={label}
            options={options}
            openOnFocus={openOnFocus}
            PaperComponent={
              creatable
                ? ({ children, ...paperProps }: HTMLAttributes<HTMLElement>) => {
                    return (
                      <Paper
                        {...paperProps}
                        sx={autocompleteProps?.componentsProps?.paper?.sx || {}}
                      >
                        {children}
                        <CreateActionDivider />
                        <CreateActionRow
                          type="button"
                          onMouseDown={(event) => {
                            // Default mousedown focuses this button, which unmounts with the popup:
                            // focus would fall off whatever the handler just focused.
                            event.preventDefault();
                            onAddNewItem?.();
                          }}
                        >
                          <Plus aria-hidden />
                          {onAddNewItemLabel}
                        </CreateActionRow>
                      </Paper>
                    );
                  }
                : undefined
            }
            value={getValue<T>(field)}
            loading={loading}
            renderOption={(props, option) => {
              const finalLabel =
                autocompleteProps?.getOptionLabel?.(option) ??
                (typeof option === 'string' ? option : option.label);
              const isEmptyOption =
                (typeof option !== 'string' && option?.id === '') || option === '';
              return (
                <li
                  {...props}
                  key={typeof option === 'string' ? option : option.id}
                  data-testid={typeof option === 'string' ? undefined : option.testId}
                >
                  <Typography noWrap>
                    {isEmptyOption ? (
                      <em style={{ fontSize: '0.875rem' }}>{finalLabel || 'Empty'}</em>
                    ) : (
                      finalLabel
                    )}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <>
                <Tooltip title={isOpened ? '' : tooltipText} {...tooltipProps}>
                  <TextField
                    error={!!errorMsg}
                    variant={variant}
                    label={labelAbove ? undefined : label}
                    {...params}
                    // `params.size` is undefined for `medium`, which the v2 TextField default turns into `small`.
                    size={size ?? params.size}
                    {...textFieldProps}
                    sx={
                      [
                        ...toSxArray(textFieldProps?.sx),
                        getFormAutocompleteMobileInputZoomSx(theme),
                      ] as SxProps<Theme>
                    }
                    inputRef={(node: HTMLInputElement | null) => {
                      setRef(inputRef, node);
                      // Lets handleSubmit focus this field when it holds the first error.
                      field.ref(node);
                    }}
                    InputProps={{
                      // MUI doesn't support `disableUnderline` for `outlined` input variant ¯\_(ツ)_/¯
                      ...(variant !== 'outlined' ? { disableUnderline: true } : {}),
                      ...params.InputProps,
                      ...inputProps,
                    }}
                  />
                </Tooltip>
                {!!underlined && !rest.hidden && (
                  <Divider sx={{ alignSelf: 'stretch', marginTop: '0.3rem' }} />
                )}
              </>
            )}
            isOptionEqualToValue={(option, value) => {
              if (typeof option !== 'string' && typeof value !== 'string') {
                return option?.id === value?.id && option?.label === value?.label;
              }
              return option === value;
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              return option?.label ?? '';
            }}
            sx={
              [
                ...toSxArray(autocompleteSx),
                getFormAutocompleteMobileInputZoomSx(theme),
              ] as SxProps<Theme>
            }
            {...restAutocompleteProps}
            slotProps={mergedSlotProps as typeof restAutocompleteProps.slotProps}
            blurOnSelect={blurOnSelect}
            onChange={(e, value, reason) => {
              if (blurOnSelect) {
                inputRef.current?.blur();
              }
              field.onChange(value);
              autocompleteProps?.onChange?.(e, value, reason);
            }}
            onBlur={(event) => {
              field.onBlur();
              autocompleteProps?.onBlur?.(event);
            }}
            onOpen={(e) => {
              autocompleteProps?.onOpen?.(e);
              setIsOpened(true);
            }}
            onClose={(e, reason) => {
              lastCloseReasonRef.current = reason;
              setIsOpened(false);
              autocompleteProps?.onClose?.(e, reason);
            }}
          />
        )}
      />
      {!!showErrorText && !!errorMsg && <FormHelperText>{String(errorMsg)}</FormHelperText>}
    </FormControl>
  );
};
